import api from "./client";
import { isRetentionQueueEnabled } from "../helpers/retentionFeature";

// C's retention queue — the human issues on an account (a complaint on a call, a
// missed visit, a balance nobody chased) with ONE next action each. Read by the
// dashboard's "At risk" tile + queue, the customer window's at-risk banner and
// its AI insights tab.
//
// FLAG-GATED, then feature-detected. The route is C's leg and is not on origin/main
// yet, and probing for it from every dashboard load is a 404 in every console — so
// nothing is asked until helpers/retentionFeature says the tenant has it. Every
// reader here answers null when the API has no such route, and the screens
// that read it render nothing (no tile, no banner, no cards) rather than an
// error. The first path that answers is remembered for the session.
//
// Shape the web renders (what is asked of C on the train):
//   GET /api/retention/queue/?customer_id=<id>&status=open   → { results: [issue] } | [issue]
//   issue = {
//     id, customer: { id, name, phone }, title,          // "Says the tech skipped the back yard"
//     quote,                                              // the person's own words, when there are any
//     source: { type: 'call'|'sms'|'email'|'visit'|'payment'|'note', at, ref },
//     status: 'open'|'in_progress'|'waiting'|'resolved',
//     opened_at, days_open, owner: { id, name },
//     sla: { state: 'ok'|'due_soon'|'breached', due_at },
//     action: { kind: 'call'|'text'|'task'|'open', label, phone },   // the one thing to do
//   }
const PATHS = ["/retention/queue/", "/customers/retention/queue/", "/comms/retention/queue/"];
let livePath = null;
let probed = null;

const rows = (data) => (Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : Array.isArray(data?.issues) ? data.issues : []);

async function resolvePath() {
  if (!isRetentionQueueEnabled()) return null; // flag off → no request at all
  if (livePath) return livePath;
  if (!probed) {
    probed = (async () => {
      for (const path of PATHS) {
        try {
          const res = await api.options(path);
          if (res.status < 400) { livePath = path; return path; }
        } catch (e) {
          const status = e?.response?.status;
          // 401/403/405 mean the route exists; 404 means keep looking.
          if (status && status !== 404) { livePath = path; return path; }
        }
      }
      return null;
    })();
  }
  return probed;
}

/** The queue, or null when this API has no retention route yet. */
export async function fetchRetentionQueue(params = {}) {
  const path = await resolvePath();
  if (!path) return null;
  try {
    const { data } = await api.get(path, { params: { status: "open", ...params } });
    return { items: rows(data), count: Number(data?.count ?? rows(data).length) || rows(data).length, path };
  } catch (e) {
    if (e?.response?.status === 404) { livePath = null; probed = null; return null; }
    throw e;
  }
}

/** Open issues on one customer, or null when the route does not exist. */
export async function fetchCustomerIssues(customerId) {
  if (!customerId) return null;
  const q = await fetchRetentionQueue({ customer_id: customerId });
  return q ? q.items : null;
}

export const issueTone = (issue) => {
  const sla = issue?.sla?.state;
  if (sla === "breached") return "danger";
  if (sla === "due_soon") return "warning";
  if (issue?.status === "resolved") return "success";
  return "neutral";
};

export const daysOpen = (issue) => {
  if (Number.isFinite(Number(issue?.days_open))) return Number(issue.days_open);
  const at = issue?.opened_at || issue?.source?.at;
  if (!at) return null;
  const d = (Date.now() - new Date(at).getTime()) / 86400000;
  return Number.isFinite(d) ? Math.max(0, Math.floor(d)) : null;
};
